import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../shared/services/UserService';
import { useAuthStore } from '../store/authStore';
import {
  Model,
  Favourite,
  DefaultAssistant,
} from '../../../shared/types/api.types';
import { useMemo } from 'react';
import { queryKeys } from '../../../shared/config/queryKeys.config';

export function useUser(options?: { enabled?: boolean }) {
  const { user: authUser } = useAuthStore();
  const queryClient = useQueryClient();
  const enabled = options?.enabled !== false;

  // Fetch full user details with a single query
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.user.byId(authUser?.uid || ''),
    queryFn: () => userService.getUser(),
    enabled: enabled && !!authUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update user's default model
  const updateUserModelMutation = useMutation({
    mutationFn: (model: Model) => {
      if (!authUser) throw new Error('User not authenticated.');
      return userService.updateUserModel(model);
    },
    onSuccess: updatedUser => {
      // Update cache directly with the response data instead of refetching
      queryClient.setQueryData(
        queryKeys.user.byId(authUser?.uid || ''),
        updatedUser
      );
    },
  });

  // Derive preferences from user data
  const preferences = user
    ? {
        temperature: user.preferred_temperature,
        k_value: user.preferred_k_value,
        system_prompt: user.preferred_system_prompt,
        default_model: user.default_model,
        default_assistant: user.default_assistant,
      }
    : null;

  const defaultAssistant: DefaultAssistant | null =
    (user?.default_assistant as DefaultAssistant | null | undefined) ?? null;

  // Extract favourites - handle both array of objects and array of strings
  const favourites = useMemo(() => {
    if (!user?.favourites) return [];

    // If favourites is an array of objects with botId property
    if (
      Array.isArray(user.favourites) &&
      user.favourites.length > 0 &&
      typeof user.favourites[0] === 'object'
    ) {
      return user.favourites.map((f: Favourite | { id: string } | string) => {
        if (typeof f === 'string') return f;
        if ('botId' in f) return f.botId;
        if ('id' in f) return f.id;
        return f;
      });
    }

    // If favourites is already an array of strings
    if (Array.isArray(user.favourites)) {
      return user.favourites;
    }

    return [];
  }, [user?.favourites]);

  return {
    user,
    preferences,
    favourites,
    defaultAssistant,
    isLoading,
    error,
    refetch,
    updateUserModel: updateUserModelMutation.mutate,
    isUpdatingModel: updateUserModelMutation.isPending,
  };
}
